import { Chip } from '@mui/material';
import { Star } from 'lucide-react';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ImageProperties from '~/components/Properties/ImageProperties';
import { ObjectDetailsDialogState } from '~/components/Properties/ObjectDetailsDialog';
import ObjectInfoButton from '~/components/Properties/ObjectInfoButton';
import { PropertiesActions } from '~/components/Properties/PropertiesPanel';
import { Image3D } from '~/domain/patient/image3d';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import { transform3DImageData } from '~/utils/transforms/imageDataTransforms';

/**
 * The (i) of an image row: it opens the image's details, with its modality
 * and slice count under the name, a Primary badge on the primary image, and
 * the rename and delete actions when given. Rename is not offered while
 * patient info is hidden, because renaming shows the series description in
 * clear and edits it.
 */
export default function ImageInfoButton({
    image,
    dialog,
    hasSharedFrameOfReference,
    onRename,
    onDelete,
}: {
    image: Image3D;
    /** The dialog state, when the row holds it; otherwise the button's own. */
    dialog?: ObjectDetailsDialogState;
    hasSharedFrameOfReference?: boolean;
    onRename?: () => void;
    onDelete?: () => void;
}) {
    const intl = useIntl();
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    const primaryImage = useSelectedPrimaryImage();
    const metadata = useMemo(() => transform3DImageData(image), [image]);

    const description =
        metadata.series.description ||
        intl.formatMessage({ id: 'unknown.series' });
    const isPrimary = primaryImage?.seriesUid === metadata.series.id;
    const rename = showSensitiveInfo ? onRename : undefined;

    return (
        <ObjectInfoButton
            titleId="image"
            dialog={dialog}
            name={description}
            subtitle={`${metadata.modality} / ${intl.formatMessage(
                { id: 'image.sliceCount' },
                { count: metadata.length }
            )}`}
            actions={
                <PropertiesActions onRename={rename} onDelete={onDelete} />
            }
            badges={
                isPrimary && (
                    <Chip
                        variant="outlined"
                        size="small"
                        color="primary"
                        icon={<Star size={12} />}
                        label={<FormattedMessage id="primary" />}
                        data-testid="primary-chip"
                    />
                )
            }
            dataset={image.firstSlice?.datasetWrapper}
        >
            <ImageProperties
                image={image}
                hasSharedFrameOfReference={hasSharedFrameOfReference}
            />
        </ObjectInfoButton>
    );
}
